// src/scenes/datahub/ImportStudents.jsx
// ★ FAST – FINAL ★

import React, { useState, useEffect, useMemo } from "react";
import {
    Box, Button, Paper, Typography, CircularProgress,
    Alert, Modal, useTheme
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UploadIcon from "@mui/icons-material/Upload";
import SendIcon from "@mui/icons-material/Send";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Fuse from "fuse.js";
import axios from "axios";

/* ───────────────────────── CONSTANT ENDPOINTS ───────────────────────── */
const SCHOOL_MST_URL = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const STUDENTS_URL = "https://figazuresql.swancreate.com/api/swa/Students";
const AI_ENDPOINT = "https://aiendpoint.swancreate.com/api/ai/gpt";

/* ───────────────────────── SMALL HELPERS ────────────────────────────── */
const money = v =>
    Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const stripeSx = theme => ({
    "& .MuiDataGrid-row:nth-of-type(even)": { background: theme.palette.action.hover },
    "& .MuiDataGrid-row:nth-of-type(odd)": { background: theme.palette.background.paper }
});

const cleanHeader = h => String(h || "")
    .replace(/\r?\n/g, " ")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

/* Our canonical target schema for FIG */
const TARGET_FIELDS = [
    "id",                 // StudentIDNumber
    "FirstName",
    "LastName",
    "Grade",
    "SiteRaw",            // the incoming site/campus field
    "PaymentStatus",      // F/R/P
    "Account_Balance",
    "guardian_FName",
    "guardian_LName",
    "guadian_Address",
    "guardian_Apt",
    "guardian_City",
    "guardian_State",
    "guardian_Zip",
    "guardian_Phone"
];

/* Heuristic mapping dictionary: common header synonyms → target field */
const HEURISTIC_MAP = [
    { target: "id", keys: ["student number", "student id", "id", "studentid", "stu id", "sid"] },
    { target: "FirstName", keys: ["first name", "firstname", "given name", "patron name first"] },
    { target: "LastName", keys: ["last name", "lastname", "surname", "family name", "patron name last"] },
    { target: "Grade", keys: ["grade", "gr", "grade level", "class"] },
    { target: "SiteRaw", keys: ["site", "school", "campus", "location", "building"] },
    { target: "PaymentStatus", keys: ["elig", "eligibility", "meal status", "meal eligibility"] },
    { target: "Account_Balance", keys: ["balance", "acct balance", "account balance", "lunch balance", "meal balance"] },
    { target: "guardian_FName", keys: ["guardian first name", "guardian fname", "parent first name", "parent fname", "guardian fname"] },
    { target: "guardian_LName", keys: ["guardian last name", "guardian lname", "parent last name", "parent lname"] },
    { target: "guadian_Address", keys: ["street address", "address", "addr", "mailing address", "home address"] },
    { target: "guardian_Apt", keys: ["apt", "apartment", "unit", "suite"] },
    { target: "guardian_City", keys: ["city", "town"] },
    { target: "guardian_State", keys: ["state", "st"] },
    { target: "guardian_Zip", keys: ["zip", "zipcode", "postal code", "zip code"] },
    { target: "guardian_Phone", keys: ["phone", "guardian phone", "parent phone", "mobile", "cell"] }
];

/* eligibility helper */
function mapEligibility(val) {
    const t = String(val ?? "").trim();
    if (!t) return "P";
    if (t === "111") return "F";
    if (t === "112") return "R";
    if (t === "113") return "P";
    if (/free/i.test(t)) return "F";
    if (/red/i.test(t)) return "R";
    return "P";
}

function parseCurrency(val) {
    if (val == null) return 0;
    if (typeof val === "number") return val;
    const s = String(val).replace(/[\$,]/g, "").trim();
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
}

/* ===================================================================== */
export default function ImportStudents() {
    const theme = useTheme();

    /* -------------------  state  ------------------- */
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);
    const [cols, setCols] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loadingParse, setLoadingParse] = useState(false);
    const [loadingAI, setLoadingAI] = useState(false);
    const [loadingPOST, setLoadingPOST] = useState(false);
    const [progressMsg, setProgressMsg] = useState("");
    const [error, setError] = useState("");

    /* -------------------  load SchoolMst once  ------------------- */
    useEffect(() => {
        axios.get(SCHOOL_MST_URL)
            .then(r => setSchools(r.data || []))
            .catch(e => setError("Failed to load SchoolMst: " + e.message));
    }, []);

    const fuse = useMemo(
        () => new Fuse(schools, { keys: ["SchoolName"], threshold: 0.30 }),
        [schools]
    );

    /* =====================================================================
       File handling: CSV or Excel → raw objects
       ===================================================================== */
    const handleFileInput = async (file) => {
        setError(""); setRows([]); setCols([]); setFileName(file.name);
        setLoadingParse(true);

        try {
            const ext = (file.name.split(".").pop() || "").toLowerCase();

            let raw = [];
            if (ext === "csv" || ext === "txt") {
                raw = await parseCSV(file);
            } else if (ext === "xls" || ext === "xlsx") {
                raw = await parseExcel(file);
            } else {
                throw new Error("Unsupported file type. Please upload CSV, XLS, or XLSX.");
            }

            if (!raw.length) {
                throw new Error("No rows parsed from file.");
            }

            // Normalize headers (remove weird whitespace/characters)
            const normalized = raw.map(r => {
                const out = {};
                Object.keys(r).forEach(k => {
                    const clean = cleanHeader(k);
                    out[clean || k] = r[k];
                });
                return out;
            });

            /* =================================================================
               AI mapping: arbitrary headers → TARGET_FIELDS
               ================================================================= */
            setLoadingAI(true);
            setProgressMsg("AI: Mapping columns…");

            const fieldMap = await getFieldMappingFromAI(normalized) || {};
            const mappedRows = applyFieldMapping(normalized, fieldMap);

            // Heuristic fallback to patch any missing targets
            const patchedRows = (mappedRows.length ? mappedRows : normalized)
                .map(row => heuristicPatch(row, normalized[0]));

            // Ensure minimal viability: id + FirstName at least
            const viable = patchedRows.filter(r => r.id && r.FirstName);
            if (!viable.length) {
                throw new Error("Could not confidently map required fields (Student ID and First Name).");
            }

            /* =================================================================
               School site AI/Fuse mapping (as before)
               ================================================================= */
            setProgressMsg("Matching schools…");
            const uniqueSites = [...new Set(viable.map(r => r.SiteRaw).filter(Boolean))];

            let siteMap = {};
            if (uniqueSites.length) {
                try {
                    const prompt = [
                        "You are given SchoolMst rows and a list of site strings.",
                        "Return ONLY JSON array [{site, SchoolName, SchoolId}] with best fuzzy match.",
                        "SchoolMst:",
                        JSON.stringify(schools),
                        "Sites:",
                        JSON.stringify(uniqueSites)
                    ].join("\n\n");

                    const { data: aiRaw } = await axios.post(AI_ENDPOINT, { prompt });
                    let txt = typeof aiRaw === "string" ? aiRaw : aiRaw.response || aiRaw;
                    txt = String(txt).replace(/```json|```/g, "").trim();
                    siteMap = Object.fromEntries(
                        JSON.parse(txt).map(o => [String(o.site || "").trim(), o])
                    );
                } catch {
                    // Fuse fallback
                    uniqueSites.forEach(s => {
                        const m = fuse.search(s).at(0);
                        if (m) siteMap[s] = {
                            site: s,
                            SchoolName: m.item.SchoolName,
                            SchoolId: m.item.SchoolId
                        };
                    });
                }
            }

            // Inject SchoolName / SchoolId; finalize rows for preview
            const finalRows = viable.map(r => {
                const m = siteMap[r.SiteRaw] || {};
                return {
                    ...r,
                    SchoolName: m.SchoolName || r.SiteRaw || "",
                    SchoolId: m.SchoolId || ""
                };
            });

            // Build columns for preview
            const previewCols = [
                { field: "id", headerName: "Student#", width: 120 },
                { field: "LastName", headerName: "Last", width: 130 },
                { field: "FirstName", headerName: "First", width: 130 },
                { field: "Grade", headerName: "Gr", width: 70 },
                { field: "SchoolName", headerName: "School", width: 220 },
                { field: "SchoolId", headerName: "School ID", width: 260 },
                { field: "PaymentStatus", headerName: "Elig", width: 80 },
                {
                    field: "Account_Balance", headerName: "Balance $", width: 110,
                    valueFormatter: p => money(p.value)
                },
                { field: "guadian_Address", headerName: "Address", width: 200 },
                { field: "guardian_Apt", headerName: "Apt", width: 70 },
                { field: "guardian_City", headerName: "City", width: 120 },
                { field: "guardian_State", headerName: "ST", width: 60 },
                { field: "guardian_Zip", headerName: "Zip", width: 90 },
                { field: "guardian_Phone", headerName: "Phone", width: 140 }
            ];

            setRows(finalRows);
            setCols(previewCols);
            setError("");
        } catch (e) {
            console.error(e);
            setError(e.message || "Failed to parse file.");
        } finally {
            setLoadingAI(false);
            setLoadingParse(false);
            setProgressMsg("");
        }
    };

    /* CSV */
    const parseCSV = (file) =>
        new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: ({ data, errors }) => {
                    if (errors?.length) return reject(new Error(errors[0].message));
                    resolve(data || []);
                },
                error: (err) => reject(err)
            });
        });

    /* Excel (xls/xlsx) */
    const parseExcel = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const wb = XLSX.read(data, { type: "array" });
                    const sheetName = wb.SheetNames[0];
                    const ws = wb.Sheets[sheetName];
                    const json = XLSX.utils.sheet_to_json(ws, {
                        raw: false,       // let SheetJS format numbers/dates
                        defval: "",       // keep empty cells as empty strings
                        cellDates: true
                    });
                    resolve(json);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

    /* =====================================================================
       AI field mapping
       ===================================================================== */
    async function getFieldMappingFromAI(normalized) {
        try {
            const sample = normalized.slice(0, 10); // send a small slice
            const headers = Object.keys(sample[0] || {});
            const prompt = [
                "You are a data transformation assistant.",
                "Map arbitrary student import columns to this FIG target schema keys:",
                JSON.stringify(TARGET_FIELDS),
                "",
                "Return ONLY JSON of the form:",
                '{ "mapping": { "<source_header>": "<target_field>", ... }, "notes": "" }',
                "",
                "Rules:",
                "- Choose the BEST match for each target if present in data.",
                "- If multiple source headers map to the same target, pick the most specific.",
                "- If a target doesn't exist in the data, omit it from mapping.",
                "- Do not invent values.",
                "",
                "Headers:",
                JSON.stringify(headers),
                "",
                "Sample Rows:",
                JSON.stringify(sample)
            ].join("\n");

            const { data: aiRaw } = await axios.post(AI_ENDPOINT, { prompt });
            let txt = typeof aiRaw === "string" ? aiRaw : aiRaw.response || aiRaw;
            txt = String(txt).replace(/```json|```/g, "").trim();

            const parsed = JSON.parse(txt);
            if (!parsed?.mapping || typeof parsed.mapping !== "object") return null;
            return parsed.mapping;
        } catch (err) {
            console.warn("AI mapping failed, falling back to heuristics.", err);
            return null;
        }
    }

    function applyFieldMapping(normalized, mapping) {
        if (!mapping) return [];
        return normalized.map(src => {
            const dest = {};
            // initialize with blanks for known fields (optional)
            TARGET_FIELDS.forEach(k => { dest[k] = ""; });
            // copy mapped values
            Object.entries(mapping).forEach(([srcKey, targetKey]) => {
                if (!targetKey) return;
                const v = src[srcKey] ?? src[cleanHeader(srcKey)] ?? "";
                dest[targetKey] = v;
            });

            // post processing for status/balance
            dest.PaymentStatus = mapEligibility(dest.PaymentStatus);
            dest.Account_Balance = parseCurrency(dest.Account_Balance);

            // if name provided in single field like "Last, First"
            if ((!dest.FirstName || !dest.LastName) && src["patron name"]) {
                const parts = String(src["patron name"]).split(",");
                dest.LastName = dest.LastName || (parts[0] || "").trim();
                dest.FirstName = dest.FirstName || (parts[1] || "").trim();
            }

            return dest;
        });
    }

    /* Heuristic patch for unmapped targets */
    function heuristicPatch(row, sampleRow) {
        const out = { ...row };
        const sampleKeys = Object.keys(sampleRow || {});
        const headerToTarget = {};

        // Build header→target map by matching cleaned header against HEURISTIC_MAP
        sampleKeys.forEach(h => {
            const ch = cleanHeader(h);
            for (const rule of HEURISTIC_MAP) {
                if (rule.keys.some(k => ch === k || ch.includes(k))) {
                    headerToTarget[h] = rule.target;
                    break;
                }
            }
        });

        // Apply heuristics only if target blanks
        Object.entries(headerToTarget).forEach(([srcH, target]) => {
            if (out[target]) return; // already filled
            const val = row[srcH] ?? row[cleanHeader(srcH)];
            if (val == null) return;
            if (target === "Account_Balance") out[target] = parseCurrency(val);
            else if (target === "PaymentStatus") out[target] = mapEligibility(val);
            else out[target] = val;
        });

        // Fallback: split "patron name"
        if ((!out.FirstName || !out.LastName) && (row["patron name"] || row["patron_name"])) {
            const pn = row["patron name"] || row["patron_name"];
            const parts = String(pn).split(",");
            out.LastName = out.LastName || (parts[0] || "").trim();
            out.FirstName = out.FirstName || (parts[1] || "").trim();
        }

        return out;
    }

    /* =====================================================================
       STEP 3 : Fast submit – patch IF student exists, else post
       ===================================================================== */
    const submitAll = async () => {
        if (!rows.length) { setError("Nothing to submit."); return; }

        setError(""); setLoadingPOST(true); setProgressMsg("Fetching existing IDs…");

        // map of existing StudentIDNumber → apiid
        let id2api = new Map();
        try {
            const { data } = await axios.get(STUDENTS_URL);
            (data || []).forEach(s => {
                if (s.StudentIDNumber) id2api.set(String(s.StudentIDNumber).trim(), s.apiid);
            });
        } catch {/* ignore */ }

        const tasks = rows
            .filter(r => r.SchoolId) // only submit rows that have a matched SchoolId
            .map(r => {
                const payload = {
                    StudentIDNumber: r.id,
                    FirstName: r.FirstName,
                    LastName: r.LastName,
                    Grade: r.Grade,
                    PaymentStatus: r.PaymentStatus,
                    Account_Balance: r.Account_Balance,
                    SchoolName: r.SchoolName,
                    SiteID: r.SchoolId,
                    guardian_FName: r.guardian_FName,
                    guardian_LName: r.guardian_LName,
                    guadian_Address: r.guadian_Address,
                    guardian_Apt: r.guardian_Apt,
                    guardian_City: r.guardian_City,
                    guardian_State: r.guardian_State,
                    guardian_Zip: r.guardian_Zip,
                    guardian_Phone: r.guardian_Phone,
                    isActive: true,
                    CreatedOn: new Date().toISOString()
                };
                const apiid = id2api.get(String(r.id).trim());
                const url = apiid ? `${STUDENTS_URL}/${apiid}` : STUDENTS_URL;
                const method = apiid ? "patch" : "post";
                return () => axios[method](url, payload);
            });

        const failures = [];
        let completed = 0;
        const POOL = 5;

        async function runNext() {
            const job = tasks.shift();
            if (!job) return;
            const idx = ++completed;
            setProgressMsg(`Uploading ${idx} / ${rows.length}`);
            try { await job(); }
            catch (err) { failures.push(err?.response?.statusText || err.message); }
            await runNext();
        }

        await Promise.all(Array(POOL).fill(0).map(runNext));

        setLoadingPOST(false); setProgressMsg("");

        if (failures.length) {
            setError(`${failures.length} upload(s) failed – see console.`);
            alert(failures.join("\n"));
        } else {
            alert("All students processed!");
            setRows([]); setFileName("");
        }
    };

    /* =====================================================================
       RENDER
       ===================================================================== */
    return (
        <Box p={3} display="flex" flexDirection="column" gap={3}>
            <Typography variant="h4" textAlign="center">
                Import Students from CSV / Excel
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            {/* upload */}
            <input
                id="file-in"
                type="file"
                accept=".csv,.txt,.xls,.xlsx"
                hidden
                onChange={e => e.target.files[0] && handleFileInput(e.target.files[0])}
            />
            <label htmlFor="file-in">
                <Button component="span" variant="contained" startIcon={<UploadIcon />}>
                    {fileName || "Upload CSV / XLS / XLSX"}
                </Button>
            </label>

            {(loadingParse || loadingAI) && <CircularProgress />}

            {/* preview */}
            {rows.length > 0 && (
                <Paper sx={{ height: "60vh", width: "100%", ...stripeSx(theme) }}>
                    <DataGrid
                        rows={rows}
                        columns={cols}
                        getRowId={(r) => r.id}
                        pageSize={25}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableSelectionOnClick
                    />
                </Paper>
            )}

            {/* submit */}
            {rows.length > 0 && (
                <Button
                    variant="contained"
                    color="success"
                    startIcon={<SendIcon />}
                    disabled={loadingPOST}
                    onClick={submitAll}
                >
                    {loadingPOST ? "Uploading…" : "Submit to FIG"}
                </Button>
            )}

            {/* progress modal */}
            <Modal
                open={loadingAI || loadingPOST}
                sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            >
                <Box sx={{
                    p: 3, bgcolor: theme.palette.background.paper,
                    borderRadius: 2, boxShadow: 24, textAlign: "center"
                }}>
                    <CircularProgress />
                    <Typography mt={2}>{progressMsg || "Processing…"}</Typography>
                </Box>
            </Modal>
        </Box>
    );
}
